import moment from 'moment';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Card from 'components/Card';
import ContentWrapper from 'components/ContentWrapper';
import DataTable, { CellPrimary } from 'components/DataTable';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import PageContainer from 'components/PageContainer';
import {
  HeaderContainer,
  HeaderTitle,
  HeaderSubtitle,
} from 'components/PageHeader';
import PageHeaderIcon from 'components/PageHeaderIcon';
import PageToolbar from 'components/PageToolbar';
import SearchInput from 'components/SearchInput';
import TableFilter from 'components/TableFilter';
import { useSearchInput, useUpdateQuery } from 'hooks/useSearchInput';
import { useGetEventsByAccountAdmin } from 'pages/app/AnnualTickets/AddAnnualTickets/gql/GetEvents';
import { useGetAnnualTickets } from 'pages/app/AnnualTickets/gql/UseGetAnnualTickets';
import { useGetAllAccounts } from 'pages/app/Credits/gql/queries/useGetAllAccounts';
import { AppContext } from 'shared/AppContext';

// Annual Type options
const AnnualTypeOptions = [
  { value: 'membership', label: 'Memberships' },
  { value: 'annual', label: 'Annual Tickets' },
];

const TrackAnnualPurchases = () => {
  const thisYear = Number(moment().format('YYYY'));

  // Applied filter state - used for the main data query (only updates on Apply)
  const [appliedYear, setAppliedYear] = useState(thisYear);
  const [appliedAccountId, setAppliedAccountId] = useState(null);
  const [appliedAnnualType, setAppliedAnnualType] = useState(null);
  const [appliedEventId, setAppliedEventId] = useState(null);

  // Pending filter state - used for populating filter options (updates on change)
  const [pendingYear, setPendingYear] = useState(thisYear);
  const [pendingAccountId, setPendingAccountId] = useState(null);
  const [pendingAnnualType, setPendingAnnualType] = useState(null);
  const [pendingEventId, setPendingEventId] = useState(null);

  const [tickets, setTickets] = useState([]);
  const [count, setCount] = useState(0);

  const { pathname } = useLocation();
  const isTrackAdmin = pathname.includes('/admin-track');
  const isEmployeeAdmin = pathname.includes('/admin-employee');
  const routePrefix = isTrackAdmin
    ? '/admin-track'
    : isEmployeeAdmin
    ? '/admin-employee'
    : '/admin';
  const userType = isTrackAdmin
    ? 'track'
    : isEmployeeAdmin
    ? 'employee'
    : 'admin';

  const { input } = useSearchInput();
  const currentPage = input.page ? Number(input.page) : 1;
  const updateQuery = useUpdateQuery();

  const {
    state: { accountId: contextAccountId, accountName },
  } = useContext(AppContext);

  // For track/employee admin, use the context accountId
  const effectiveAccountId =
    userType !== 'admin' ? contextAccountId : appliedAccountId;

  // Fetch accounts for admin filter (only for admin users)
  const { data: accountsData, loading: accountsLoading } = useGetAllAccounts({
    skip: userType !== 'admin',
  });

  // Fetch events when annual type is 'registration' and account is selected
  const { data: eventsData, loading: eventsLoading } =
    useGetEventsByAccountAdmin(
      pendingAnnualType === 'registration' && pendingAccountId
        ? pendingAccountId
        : null
    );

  // Main data query
  const { res: data, loading } = useGetAnnualTickets(
    effectiveAccountId,
    true, // isPurchase
    userType,
    appliedAnnualType ? { value: appliedAnnualType } : null,
    appliedEventId,
    appliedYear
  );

  useEffect(() => {
    if (data?.getAnnualTickets) {
      setTickets(data.getAnnualTickets.results || []);
      setCount(data.getAnnualTickets.count || 0);
    }
  }, [data]);

  // Handle search
  const handleSearch = useCallback(
    (searchTerm) => {
      const currentQueryString = input.queryString || '';
      if (searchTerm !== currentQueryString) {
        updateQuery({ queryString: searchTerm, page: 1 });
      }
    },
    [updateQuery, input.queryString]
  );

  // Handle pagination
  const handlePageChange = (page) => {
    const totalPages = Math.ceil(count / 15);
    if (page < 1 || page > totalPages) return;
    updateQuery({ page });
  };

  // Handle filter change - updates pending state for dynamic filter options
  const handleFilterChange = (newFilters) => {
    if (newFilters.year !== undefined) {
      setPendingYear(Number(newFilters.year) || thisYear);
    }
    if (newFilters.accountId !== undefined) {
      setPendingAccountId(newFilters.accountId || null);
      // Reset dependent filters when account changes
      if (newFilters.accountId !== pendingAccountId) {
        setPendingAnnualType(null);
        setPendingEventId(null);
      }
    }
    if (newFilters.annualType !== undefined) {
      setPendingAnnualType(newFilters.annualType || null);
      // Reset event when type changes
      if (newFilters.annualType !== 'registration') {
        setPendingEventId(null);
      }
    }
    if (newFilters.eventId !== undefined) {
      setPendingEventId(newFilters.eventId || null);
    }
  };

  // Handle filter apply - updates applied state to trigger main query
  const handleApplyFilters = (newFilters) => {
    const newYear = newFilters.year ? Number(newFilters.year) : pendingYear;
    const newAccountId = newFilters.accountId || pendingAccountId;
    const newAnnualType = newFilters.annualType || pendingAnnualType;
    const newEventId = newFilters.eventId || pendingEventId;

    // Update applied state
    setAppliedYear(newYear);
    setAppliedAccountId(newAccountId);
    setAppliedAnnualType(newAnnualType);
    setAppliedEventId(newEventId);

    // Sync pending state with applied state
    setPendingYear(newYear);
    setPendingAccountId(newAccountId);
    setPendingAnnualType(newAnnualType);
    setPendingEventId(newEventId);

    updateQuery({ page: 1 });
  };

  // Handle filter clear
  const handleClearFilters = () => {
    // Reset both applied and pending state
    setAppliedYear(thisYear);
    setAppliedAccountId(null);
    setAppliedAnnualType(null);
    setAppliedEventId(null);
    setPendingYear(thisYear);
    setPendingAccountId(null);
    setPendingAnnualType(null);
    setPendingEventId(null);
    updateQuery({ page: 1 });
  };

  // Build filter configuration for TableFilter
  const filterConfig = [
    {
      key: 'year',
      label: 'Year',
      type: 'select',
      placeholder: 'Select Year',
      options: [
        { value: thisYear - 2, label: String(thisYear - 2) },
        { value: thisYear - 1, label: String(thisYear - 1) },
        { value: thisYear, label: String(thisYear) },
        { value: thisYear + 1, label: String(thisYear + 1) },
      ],
    },
  ];

  // Add account filter for admin users only
  if (userType === 'admin') {
    if (accountsLoading) {
      filterConfig.push({
        key: 'accountId',
        label: 'Account',
        type: 'select',
        placeholder: 'All Accounts',
        options: [],
        loading: true,
        loadingText: 'Loading accounts...',
      });
    } else if (accountsData?.getAllAccounts?.length > 0) {
      filterConfig.push({
        key: 'accountId',
        label: 'Account',
        type: 'select',
        placeholder: 'All Accounts',
        options: accountsData.getAllAccounts.map((account) => ({
          value: account.id,
          label: account.business_dba,
        })),
      });
    }
  }

  // Add annual type filter (show when account is selected for admin, or always for track/employee)
  if (userType !== 'admin' || pendingAccountId) {
    filterConfig.push({
      key: 'annualType',
      label: 'Annual Type',
      type: 'select',
      placeholder: 'All Types',
      options: AnnualTypeOptions,
    });
  }

  // Add event filter only when annual type is 'registration'
  if (pendingAnnualType === 'registration' && pendingAccountId) {
    if (eventsLoading) {
      filterConfig.push({
        key: 'eventId',
        label: 'Event',
        type: 'select',
        placeholder: 'All Events',
        options: [],
        loading: true,
        loadingText: 'Loading events...',
      });
    } else if (eventsData?.getEventsByAccountAdmin?.results?.length > 0) {
      filterConfig.push({
        key: 'eventId',
        label: 'Event',
        type: 'select',
        placeholder: 'All Events',
        options: eventsData.getEventsByAccountAdmin.results.map((event) => ({
          value: event.id,
          label: event.isMultiDay
            ? `${event.month} ${event.listDates} - ${moment(
                event.fullDate,
                'ddd, MMM D, YYYY'
              ).format('YYYY')}`.toUpperCase() + ` — ${event.name}`
            : `${moment(event.fullDate, 'ddd, MMM D, YYYY').format(
                'ddd MMM D - YYYY'
              )}`.toUpperCase() + ` — ${event.name}`,
        })),
      });
    }
  }

  // Current filter values for TableFilter
  const filterValues = {
    year: pendingYear,
    accountId: pendingAccountId || '',
    annualType: pendingAnnualType || '',
    eventId: pendingEventId || '',
  };

  // DataTable columns
  const columns = [
    {
      field: 'track',
      header: 'Track / Series',
      render: (ticket) =>
        ticket.tracks?.length > 0
          ? ticket.tracks[0]?.name
          : ticket.series?.[0]?.name || '-',
    },
    {
      field: 'account',
      header: 'Account',
      render: (ticket) => ticket.account?.business_dba || '-',
    },
    {
      field: 'name',
      header: 'Annual / Season Ticket Name',
      mobileTitle: true,
      render: (ticket) => <CellPrimary>{ticket.name}</CellPrimary>,
    },
  ];

  // DataTable actions
  const actions = [
    {
      type: 'view',
      title: 'View Purchases',
      mobileLabel: 'View',
      to: (ticket) =>
        `${routePrefix}/annualPurchases/purchase/${ticket.id}?ticketName=${ticket.name}&&accountName=${ticket.account?.business_dba}&&account_id=${ticket.account?.id}`,
    },
  ];

  const totalPages = Math.ceil(count / 15);

  // Get filter description for subtitle
  const getFilterDescription = () => {
    const parts = [];
    if (appliedAnnualType) {
      const type = AnnualTypeOptions.find((t) => t.value === appliedAnnualType);
      if (type) parts.push(type.label);
    }
    if (parts.length === 0) parts.push('All Purchases');
    return parts.join(' - ');
  };

  return (
    <EnhancedThemeProvider>
      <PageContainer>
        {/* Header Card */}
        <Card style={{ marginBottom: '30px' }}>
          <HeaderContainer>
            <PageHeaderIcon icon="events" />
            <div>
              <HeaderTitle>Annual / Season Purchases</HeaderTitle>
              <HeaderSubtitle>
                {accountName || 'All Accounts'} - {appliedYear} -{' '}
                {getFilterDescription()}
              </HeaderSubtitle>
            </div>
          </HeaderContainer>
        </Card>

        {/* Content Card */}
        <Card noPadding>
          <ContentWrapper>
            {/* Page Header with Search and Filters */}
            <PageToolbar>
              <SearchInput
                onSearch={handleSearch}
                placeholder="Search by Ticket Name..."
                debounceMs={500}
              />
              <TableFilter
                filters={filterConfig}
                values={filterValues}
                onChange={handleFilterChange}
                onApply={handleApplyFilters}
                onClear={handleClearFilters}
              />
            </PageToolbar>

            {/* DataTable */}
            <DataTable
              columns={columns}
              data={tickets}
              loading={loading}
              keyField="id"
              emptyMessage="No annual tickets found"
              emptySubtext="Try adjusting your search or filters"
              actions={actions}
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </ContentWrapper>
        </Card>
      </PageContainer>
    </EnhancedThemeProvider>
  );
};

export default TrackAnnualPurchases;
